// INSIDO: insido design tokens (07-tema-insido).
//
// T-01 decision — EXPLICIT OVERRIDE, not a new Material 3 seed.
// The client builds its palette from a single M3 seed (see materialTheme.ts),
// but a tonal seed cannot reproduce the prototype's exact neutral surfaces
// (#181819 channels, #141415 chat, #111111 ink, ...). So we keep M3 for the
// accent-derived tokens (primary/secondary/tertiary come from the blue seed
// #3c7de6 set in components/branding) and override ONLY the neutral surface /
// outline / on-surface ramp with the prototype's exact hex, for both themes.
//
// These overrides are applied in LoadTheme AFTER the generated M3 variables so
// they win. We also expose the prototype's semantic tokens as `--insido-*` and
// the 4 brand colours as `--brand-*` (single point of truth for module 08).
//
// Source: docs/mockup/prototipo-v3.html  :root (dark) and body.light (light).

/** Prototype palette, dark theme (`:root`) */
const DARK = {
  ink: "#111111",
  inset: "#0c0c0c",
  chatBg: "#141415",
  surface: "#181819",
  card2: "#1b1b1d",
  raised: "#202022",
  raised2: "#29292c",
  line: "#313135",
  lineSoft: "#1e1e20",
  text: "#f1f1f2",
  text2: "#d3d3d6",
  muted: "#8c8c93",
  faint: "#5a5a60",
  accent: "#3c7de6",
  accentBright: "#5a93ec",
  accentSoft: "rgba(60, 125, 230, 0.15)",
  accentLine: "rgba(60, 125, 230, 0.34)",
  brandRed: "#ec6359",
  brandAmber: "#f2b550",
  brandGreen: "#4cb878",
  brandBlue: "#5a93ec",
  markHead: "#f1f1f2",
} as const;

/** Prototype palette, light theme (`body.light`) */
const LIGHT = {
  ink: "#e3e3e5",
  inset: "#ffffff",
  chatBg: "#ffffff",
  surface: "#f5f5f6",
  card2: "#ffffff",
  raised: "#ececee",
  raised2: "#e2e2e5",
  line: "#d8d8db",
  lineSoft: "#e7e7ea",
  text: "#16161a",
  text2: "#33333a",
  muted: "#6b6b70",
  faint: "#9a9aa0",
  accent: "#3c7de6",
  accentBright: "#2f6fd6",
  accentSoft: "rgba(60, 125, 230, 0.12)",
  accentLine: "rgba(60, 125, 230, 0.30)",
  brandRed: "#e0483d",
  brandAmber: "#cf8f1e",
  brandGreen: "#2f9d5b",
  brandBlue: "#3c7de6",
  markHead: "#111111",
} as const;

type Palette = Record<keyof typeof DARK, string>;

// INSIDO (18-feedback-teste): the accent is user-selectable (Aspeto → "Cor de
// destaque", stored as `theme.m3Accent`). Every accent-derived token comes from
// one hex; the hand-tuned prototype values are kept for the default blue.

/** The prototype accent; `theme.m3Accent` defaults to it (BRANDING) */
export const DEFAULT_ACCENT = "#3c7de6";

/** Parse "#rgb"/"#rrggbb" to [r, g, b]; undefined when not a hex colour */
function rgbOf(hex: string): [number, number, number] | undefined {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return undefined;
  const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

/** Mix a colour towards white (t > 0) or black (t < 0) */
function mix(rgb: [number, number, number], t: number): string {
  const target = t > 0 ? 255 : 0;
  const k = Math.abs(t);
  return (
    "#" +
    rgb
      .map((c) =>
        Math.round(c + (target - c) * k)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}

/**
 * Accent family (accent, bright, soft, line) for a chosen hex
 * @param accent "#rrggbb"
 * @param darkMode Whether the dark theme is active
 */
export function deriveAccent(
  accent: string,
  darkMode: boolean,
): Pick<Palette, "accent" | "accentBright" | "accentSoft" | "accentLine"> {
  const base = darkMode ? DARK : LIGHT;
  const rgb = rgbOf(accent);
  if (!rgb || accent.toLowerCase() === DEFAULT_ACCENT) {
    return {
      accent: base.accent,
      accentBright: base.accentBright,
      accentSoft: base.accentSoft,
      accentLine: base.accentLine,
    };
  }
  const [r, g, b] = rgb;
  return {
    accent: mix(rgb, 0),
    // hover/bright: lighter on dark, deeper on light (as the prototype pair)
    accentBright: mix(rgb, darkMode ? 0.18 : -0.14),
    accentSoft: `rgba(${r}, ${g}, ${b}, ${darkMode ? 0.15 : 0.12})`,
    accentLine: `rgba(${r}, ${g}, ${b}, ${darkMode ? 0.34 : 0.3})`,
  };
}

/** Convert "#rrggbb" to an "r, g, b" triplet (for the --mdui-color-* vars) */
function triplet(hex: string): string {
  const m = /#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  if (!m) return "0, 0, 0";
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

/**
 * Map the prototype neutrals onto the Material 3 surface ramp. Wherever an
 * insido surface should appear, the app already reads one of these M3 tokens,
 * so overriding them re-skins the whole chrome with the prototype neutrals.
 */
function materialSurfaceOverrides(p: Palette): Record<string, string> {
  return {
    surface: p.surface,
    "surface-dim": p.chatBg,
    "surface-bright": p.raised2,
    "surface-container-lowest": p.inset,
    "surface-container-low": p.chatBg,
    "surface-container": p.card2,
    "surface-container-high": p.raised,
    "surface-container-highest": p.raised2,
    "on-surface": p.text,
    "on-surface-variant": p.muted,
    outline: p.line,
    "outline-variant": p.lineSoft,
  };
}

// INSIDO (14-polish-superficies, RF-01): the accent-derived M3 roles were left
// to the tonal seed by module 07, and the seed's secondary/tertiary/inverse
// families are the grey-blue Stoat surfaces still visible in the settings
// modal, dialogs, menus, chips, snackbars and the friends page. Map EVERY M3
// colour role the client reads onto the prototype tokens as well.

/** Error tint, dark + light (brand red family) */
const ERROR = {
  dark: {
    error: "#ec6359", // --brand-red
    container: "rgba(236, 99, 89, 0.16)",
  },
  light: {
    // --brand-red light (#e0483d) is 4.1:1 on white; one step darker keeps
    // error TEXT at AA (5.2:1 on #ffffff) while staying in the brand family.
    error: "#c9372c",
    container: "rgba(201, 55, 44, 0.12)",
  },
} as const;

/**
 * Map the accent / secondary / tertiary / error / inverse M3 roles onto the
 * prototype tokens. Values may be rgba() (translucent tints read correctly on
 * any surface); the mdui triplets get an opaque equivalent over `surface`.
 * @param p Active palette
 * @param darkMode Whether the dark theme is active
 */
function materialRoleOverrides(
  p: Palette,
  darkMode: boolean,
): Record<string, string> {
  const err = darkMode ? ERROR.dark : ERROR.light;
  // the inverse surface is the "other" theme's raised tone (snackbars,
  // spoilers, neutral permission override)
  // INSIDO (18-feedback-teste): the inverse primary follows the chosen accent
  const inversePrimary = deriveAccent(p.accent, true).accentBright;
  const inverse = darkMode
    ? { surface: DARK.raised2, on: DARK.text, primary: inversePrimary }
    : { surface: DARK.card2, on: DARK.text, primary: inversePrimary };

  return {
    // accent = interaction (buttons, links, focus, active)
    primary: p.accent,
    "on-primary": "#ffffff",
    "primary-container": p.accentSoft, // mentions, reply preview, highlights
    "on-primary-container": p.text,
    "inverse-primary": inverse.primary,
    "surface-tint": p.accent,

    // secondary = neutral tonal (tonal buttons, selected icon buttons,
    // reactions, blockquotes, nav indicator). raised-2 so a tonal control is
    // still visible on surface, chat-bg AND inside a raised dialog.
    secondary: p.muted,
    "on-secondary": darkMode ? p.ink : "#ffffff",
    "secondary-container": p.raised2,
    "on-secondary-container": p.text,

    // tertiary = option rows of collapsible selects (CategoryButton
    // tertiary/tertiaryAlt) + nested blockquotes: neutral row, accent when
    // selected — the brand amber stays reserved for presence/away.
    tertiary: p.accent,
    "on-tertiary": "#ffffff",
    "tertiary-container": p.card2,
    "on-tertiary-container": p.text,

    // error = brand red
    error: err.error,
    "on-error": "#ffffff",
    "error-container": err.container,
    "on-error-container": p.text,

    // neutral variants
    "surface-variant": p.raised,
    "on-surface-container": p.text,
    "inverse-surface": inverse.surface,
    "inverse-on-surface": inverse.on,

    shadow: "#000000",
    scrim: "#000000",
  };
}

/**
 * Opaque "r, g, b" triplet for a colour, flattening rgba() over a base hex.
 * @param colour "#rrggbb" or "rgba(r, g, b, a)"
 * @param base Opaque "#rrggbb" to composite translucent colours over
 */
function solidTriplet(colour: string, base: string): string {
  const m = /rgba\(\s*(\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\s*\)/.exec(colour);
  if (!m) return triplet(colour);
  const a = parseFloat(m[4]);
  const b = triplet(base).split(",").map(Number);
  return [m[1], m[2], m[3]]
    .map((c, i) => Math.round(Number(c) * a + b[i] * (1 - a)))
    .join(", ");
}

/**
 * Build every insido CSS variable for the active theme.
 * @param darkMode Whether the dark theme is active
 * @param accent INSIDO (18-feedback-teste): chosen accent hex (default blue)
 */
export function createInsidoVariables(
  darkMode: boolean,
  accent: string = DEFAULT_ACCENT,
): Record<string, string> {
  const p: Palette = {
    ...(darkMode ? DARK : LIGHT),
    ...deriveAccent(accent, darkMode),
  };

  const out: Record<string, string> = {
    // --- semantic prototype tokens (consumed by insido components + module 08)
    "--insido-ink": p.ink,
    "--insido-inset": p.inset,
    "--insido-chat-bg": p.chatBg,
    "--insido-surface": p.surface,
    "--insido-card-2": p.card2,
    "--insido-raised": p.raised,
    "--insido-raised-2": p.raised2,
    "--insido-line": p.line,
    "--insido-line-soft": p.lineSoft,
    "--insido-text": p.text,
    "--insido-text-2": p.text2,
    "--insido-muted": p.muted,
    "--insido-faint": p.faint,
    "--insido-accent": p.accent,
    "--insido-accent-bright": p.accentBright,
    "--insido-accent-soft": p.accentSoft,
    "--insido-accent-line": p.accentLine,
    "--insido-mark-head": p.markHead,

    // --- 4 brand colours (single point of truth, consumed by module 08)
    "--brand-red": p.brandRed,
    "--brand-amber": p.brandAmber,
    "--brand-green": p.brandGreen,
    "--brand-blue": p.brandBlue,

    // --- presence = brand colours (light/dark aware), see UserStatus.tsx
    "--brand-presence-online": p.brandGreen,
    "--brand-presence-idle": p.brandAmber,
    "--brand-presence-busy": p.brandRed,
    "--brand-presence-focus": p.brandBlue,

    // --- shape (RF-06)
    "--radius": "18px",
    "--radius-pill": "999px",
  };

  // Override the M3 neutral ramp and every accent/role colour (both the
  // --md-sys-color vars and the mdui triplets).
  for (const [key, colour] of Object.entries({
    ...materialSurfaceOverrides(p),
    ...materialRoleOverrides(p, darkMode),
  })) {
    out[`--md-sys-color-${key}`] = colour;
    out[`--mdui-color-${key}`] = solidTriplet(colour, p.surface);
  }

  return out;
}
